import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {BmDunningAssignPaymentsService} from '../../service/bm-dunning-assign-payments.service';
import {DunningData} from '../../../../../activity/payment-plan/model/dunning-data.interface';
import {Contract} from '../../../../../activity/payment-plan/model/contract.interface';

@Component({
    selector: 'bm-tool-panel',
    templateUrl: './tool-panel.component.html',
    styleUrls: ['./tool-panel.component.scss'],
    standalone: false
})
export class ToolPanelComponent implements OnInit, OnDestroy {

    @Input() visible: boolean = false;
    @Output() close = new EventEmitter<void>();

    dunningData: DunningData | null = null;
    contracts: Contract[] = [];

    private subscriptions: Subscription[] = [];

    constructor(private assignPaymentsService: BmDunningAssignPaymentsService) {
    }

    ngOnInit(): void {
        this.subscribeToService();
    }

    /**
     * Subscribe to service observables
     */
    private subscribeToService(): void {
        // Subscribe to tool panel visibility
        this.subscriptions.push(
            this.assignPaymentsService.toolPanelVisible$.subscribe(
                (visible) => {
                    this.visible = visible;
                }
            )
        );

        // Subscribe to dunning data
        this.subscriptions.push(
            this.assignPaymentsService.dunningData$.subscribe(
                (data) => {
                    this.dunningData = data;
                    if (data) {
                        this.contracts = data.contracts || [];
                    }
                }
            )
        );
    }

    /**
     * Close the panel
     */
    onClose(): void {
        this.assignPaymentsService.closeToolPanel();
        this.close.emit();
    }

    /**
     * Get contract badge
     */
    getContractBadge(contract: Contract): 'STORNO' | 'WIK' | 'NEU' {
        if (contract.status === 'new') return 'NEU';
        if (contract.status === 'cancelled') return 'STORNO';
        // For other statuses (in-dunning, reactivated), default to WIK
        return 'WIK';
    }

    /**
     * Get contract badge class
     */
    getContractBadgeClass(contract: Contract): string {
        if (contract.status === 'new') return 'neu';
        if (contract.status === 'cancelled') return 'storno';
        // For other statuses (in-dunning, reactivated), default to wik
        return 'wik';
    }

    /**
     * Get contract monthly price
     */
    getContractMonthlyPrice(contract: Contract): number {
        if (contract.monthlyPrice && contract.monthlyPrice.length > 0) {
            return contract.monthlyPrice[contract.monthlyPrice.length - 1].monthlyPrice;
        }
        return 0;
    }

    /**
     * Get WIK date
     */
    getWikDate(contract: Contract): string {
        if (contract.status === 'new') {
            // For new contracts, show start date based on monthlyPrice startMonth
            const sm = contract.monthlyPrice?.[0]?.startMonth; // YYYY-MM format
            return this.formatYearMonthToDate(sm);
        }

        // Calculate WIK date based on dunning and reactivation months
        const months = (contract.dunningMonths ?? 0) + (contract.reactivationMonths ?? 0);
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() + months);
        return this.formatDate(d);
    }

    /**
     * Format year-month string to date string
     */
    private formatYearMonthToDate(ym?: string): string {
        // ym = "2026-01" → 01.01.2026
        if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return '';
        const [y, m] = ym.split('-').map(Number);
        const d = new Date(y, m - 1, 1);
        return this.formatDate(d);
    }

    /**
     * Format date to DD.MM.YYYY
     */
    private formatDate(d: Date): string {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    }

    /**
     * Destroy component
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}

